import React, { createContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Snackbar from "@material-ui/core/Snackbar";
import Button from "@material-ui/core/Button";
import MuiAlert from "@material-ui/lab/Alert";
import { useTranslate } from "react-polyglot";

/**
 * Провайдер, который периодически опрашивает файл /version.json и определяет,
 * изменилась ли версия фронтенда. Если обнаружено обновление — показывает
 * сообщение (Snackbar) с кнопкой «Перезагрузить». По нажатию страница
 * перезагружается, подгружая новую версию приложения.
 */

export const AppVersionContext = createContext({ hasNewVersion: false });

/**
 * Компонент Alert из material-ui/lab, обёрнутый для удобства.
 */
function Alert(props) {
  // elevation=6 и variant="filled" соответствуют дизайну остальных Snackbar-ов в проекте
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <MuiAlert elevation={6} variant="filled" {...props} />;
}

const FETCH_INTERVAL_MS = 60000; // 1 минута
// const FETCH_INTERVAL_MS = 1000; // 1 секунда

/** */
const AppVersionProvider = ({ children }) => {
  const t = useTranslate();
  const [currentHash, setCurrentHash] = useState(null);
  const [hasNewVersion, setHasNewVersion] = useState(false);

  // Чтобы избежать гонок при асинхронных запросах, храним актуальное значение в ref
  const hashRef = useRef(null);
  hashRef.current = currentHash;

  /**
   * Загружает /version.json и сравнивает полученный хэш с сохранённым.
   */
  async function checkVersion() {
    try {
      // Добавляем параметр ?cb=<timestamp>, чтобы обойти кеш браузера
      const response = await fetch(`/version.json?cb=${Date.now()}`, {
        cache: "no-store",
      });

      if (!response.ok) return; // Сервер вернул ошибку — просто игнорируем

      /** @type {{ hash?: string }} */
      const data = await response.json();
      if (!data?.hash) return; // Файл имеет неожиданную структуру

      // Первая успешная загрузка — запоминаем хэш и выходим
      if (hashRef.current === null) {
        setCurrentHash(data.hash);
        return;
      }

      // Если хэш изменился — уведомляем пользователя
      if (data.hash !== hashRef.current) {
        setHasNewVersion(true);
      }
    } catch (e) {
      // Сеть недоступна / JSON невалиден — пропускаем и пробуем позже
      // Можно добавить логирование при необходимости
    }
  }

  // Проверяем версию сразу после маунта, а затем каждые FETCH_INTERVAL_MS
  useEffect(() => {
    checkVersion();
    const timerId = setInterval(checkVersion, FETCH_INTERVAL_MS);
    return () => clearInterval(timerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Перезагружает страницу целиком, чтобы подгрузить свежую версию.
   */
  function handleReload() {
    window.location.reload();
  }

  return (
    <AppVersionContext.Provider value={{ hasNewVersion }}>
      {children}
      <Snackbar open={hasNewVersion} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
        <Alert
          severity="info"
          action={
            <Button color="inherit" size="small" onClick={handleReload}>
              {t("version.button")}
            </Button>
          }
        >
          {t("version.message")}
        </Alert>
      </Snackbar>
    </AppVersionContext.Provider>
  );
};

AppVersionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AppVersionProvider;
